import Vue from 'vue'
import Router from 'vue-router'

import { PageNotFound } from 'common-views'
import { permissionsMixin } from 'common-mixins/permissions'
import { isKeycloakTokenExpiredSafe } from '@utils/keycloakUtils'
import { getAssetRoute } from './utils/routeUtils'

const searchReadPermissionByRouteName =
  permissionsMixin.methods.searchReadPermissionByRouteName

// TODO: why do we need this to have access to router methods in components ???
Vue.use(Router)

export const createRouter = (kuzzle, keycloak, store, backendName) => {
  const hasPermission = (pathName) => {
    const findReadPermission = searchReadPermissionByRouteName(
      store.getters['auth/currentPermissions'],
      pathName,
    )
    return findReadPermission === null ? false : findReadPermission.value
  }

  const adminGuard = async (to, _from, next) => {
    if (store.getters['auth/isAdmin']) {
      next()
    } else {
      next({ name: 'notFound', params: { 0: to.path } })
    }
  }

  const nonWorkerProfileGuard = async (to, _from, next) => {
    if (
      !store.getters['auth/isAdmin'] &&
      !store.getters['auth/isPreperer'] &&
      store.getters['auth/isWorker']
    ) {
      next({ name: 'notFound', params: { 0: to.path } })
    } else {
      next()
    }
  }

  // WARN: careful use of next(), it is flawky & dangerous, see docs: https://router.vuejs.org/guide/advanced/navigation-guards.html#Optional-third-argument-next
  const authenticationGuard = async (to, _from, next) => {
    try {
      const { hub_user: hubTransmissibleId } = to.query

      if (hubTransmissibleId) {
        const hubUserTransmissibleIdFromStore =
          store.getters['auth/hubUserTransmissibleId']
        const hubUserFromStore = store.getters['auth/hubUser']

        let keycloakProfile = null

        try {
          if (keycloak.authenticated) {
            keycloakProfile = await keycloak.loadUserProfile()
          }
        } catch (loadProfileError) {
          console.warn(
            '[router.authenticationGuard] unable to load KC profile for user:',
            loadProfileError,
          )
        }

        if (
          hubUserTransmissibleIdFromStore === hubTransmissibleId &&
          keycloak.authenticated &&
          !isKeycloakTokenExpiredSafe(keycloak) &&
          hubUserFromStore?.email === keycloakProfile?.email
        ) {
          console.debug(
            '[router/authenticationGuard] hub_user === currently stored hub transmissible id & KC token. skipping re-authentication.',
          )
        } else if (keycloak.authenticated) {
          console.warn(
            '[router.authenticationGuard] local keycloak user is not the same as hub_user in param, disconnecting current user & KC session ...',
          )

          // HACK: clean-up hub widget store to force re-authent with different user when hub user token mismatch
          // (should be handled by widget in the future)
          localStorage.removeItem('hubUserState')

          await store.dispatch('auth/GLOBAL_LOG_OUT', {
            kuzzle,
            keycloak,
            // HACK: if keycloak is authenticated, we want to show the logout prompt to make sure we kill the current session.
            // otherwise, we want to redirect to currentPage
            showLogoutPrompt: keycloak.authenticated,
          })

          // cancel current nav
          return false
        } else {
          console.debug(
            '[router.authenticationGuard] no KC OR hub user currently stored, fetching hub_user with tokens ...',
          )
          // once hub user fetched & KC tokens OK, let kuzzle check happen normally
          // indeed, if successful, check_keycloak_token will succeed (keycloak should have updated internal tokens)
          // and we proceed to check kuzzle auth.
          const hubUser = await store.dispatch('auth/FETCH_HUB_USER', {
            keycloak,
            hubTransmissibleId,
            backendName,
          })

          if (!hubUser) {
            console.error(
              '[router.authenticationGuard] failure to fetch hub user & validate KC tokens, triggering logout...',
            )

            // global logout will clean internal state for hub user & tokens
            await store.dispatch('auth/GLOBAL_LOG_OUT', {
              kuzzle,
              keycloak,
              // HACK: if keycloak is authenticated, we want to show the logout prompt to make sure we kill the current session.
              // otherwise, we want to redirect to currentPage
              showLogoutPrompt: keycloak.authenticated,
            })

            // stop nav
            return false
          }

          console.debug(
            '[router.authenticationGuard] hub user fetched with valid KC tokens:',
            hubUser,
          )
        }
      }

      let checkKeycloakTokenResult = await store.dispatch(
        'auth/CHECK_KEYCLOAK_TOKEN',
        {
          keycloak,
          backendName,
        },
      )

      if (!checkKeycloakTokenResult) {
        console.warn(
          '[router.authenticationGuard] no KC user found in store (or KC user has expired access token), redirect to KC login page',
          checkKeycloakTokenResult,
        )

        // disconnect from both kuzzle & KC, redirect to KC login page
        await store.dispatch('auth/GLOBAL_LOG_OUT', {
          kuzzle,
          keycloak,
          // if KC is still authenticated (although token is expired),
          // we need to redirect user to keycloak logout prompt to kill the current session:
          // otherwise, potential infinite loop if a problem happens during auth
          showLogoutPrompt: keycloak.authenticated,
        })

        // stop current navigation flow
        return false
      } else if (await store.dispatch('auth/CHECK_KUZZLE_TOKEN', kuzzle)) {
        // check if current kuzzle SDK has valid tokens to connect to kuzzle backend.

        if (!store.getters['auth/currentDefaultPage']) {
          next()
        } else if (!hasPermission(to.name) && to.name !== 'redirect-qr') {
          const pageName = store.getters['auth/currentDefaultPage']
          next({ name: pageName })
        } else {
          next()
        }
      } else {
        console.warn(
          `[router.authenticationGuard][${to.path}][${_from.path}] CHECK_KUZZLE_TOKEN failure, try to re-connect to kuzzle in the background.`,
        )

        try {
          // TODO: does this handle refresh token the same way ??
          await store.dispatch('auth/KUZZLE_LOGIN', {
            kuzzle,
            strategy: 'keycloak',
            keycloak,
          })
        } catch (kuzzleLoginError) {
          console.error(
            '[router.authenticationGuard] failure during KUZZLE_LOGIN. aborting.',
            kuzzleLoginError,
          )

          await store.dispatch('auth/GLOBAL_LOG_OUT', {
            kuzzle,
            keycloak,
            // if KC is authenticated (should be the case here),
            // we need to redirect user to keycloak logout prompt to kill the current session:
            // otherwise, potential infinite loop if a problem happens during auth
            showLogoutPrompt: keycloak.authenticated,
          })

          // stop current navigation flow
          return false
        }

        next()
      }
    } catch (error) {
      console.error(
        `[router.authenticationGuard][Error][${to.path}][${_from.path}] error during auth check, redirecting to login page.`,
        error,
      )

      // disconnect from both kuzzle & KC, redirect to KC login page
      await store.dispatch('auth/GLOBAL_LOG_OUT', {
        kuzzle,
        keycloak,
        // if KC is still authenticated, we need to redirect user to keycloak logout prompt to kill the current session:
        // otherwise, potential infinite loop if a problem happens during auth
        showLogoutPrompt: keycloak.authenticated,
      })

      // stop current navigation flow
      return false
    }
  }

  const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
      {
        path: '/',
        // TODO: we should redirect to defaultPage is homePage feature not enabled for current customer
        beforeEnter: authenticationGuard,
        component: () => import('@components/DefaultContainer.vue'),
        children: [
          {
            path: '',
            name: 'homePage',
            component: () => import('@views/HomePage.vue'),
          },
        ],
      },
      {
        path: '/asset',
        beforeEnter: authenticationGuard,
        component: () => import('@components/DefaultContainer.vue'),
        children: [
          // default route for /asset should automatically redirect to currently set tracking view mode
          // OR defaultRoute set in permissions if no default viewMode is set yet
          {
            path: '',
            redirect: (_to) => {
              return getAssetRoute(
                store.getters['app/viewMap'],
                store.getters['auth/currentDefaultPage'],
              )
            },
          },
          {
            path: ':defaultMode/:customerName?',
            name: 'asset',
            component: () => import('@views/Tracking.vue'),
            props: (route) => route.params,
          },
          {
            path: ':customerName/view/:assetId',
            name: 'asset-view',
            component: () => import('@components/asset/AssetView.vue'),
          },
        ],
      },
      {
        path: '/live-site',
        beforeEnter: authenticationGuard,
        component: () => import('@components/DefaultContainer.vue'),
        children: [
          {
            path: 'safety-dashboard',
            name: 'safetyDashboard',
            component: () => import('@views/SafetyDashboard.vue'),
          },
          {
            path: 'data-quality',
            name: 'dataQuality',
            component: () => import('@views/DataQuality.vue'),
          },
        ],
      },
      {
        path: '/dashboard',
        beforeEnter: authenticationGuard,
        component: () => import('@components/DefaultContainer.vue'),
        children: [
          {
            path: 'overview',
            name: 'overview',
            component: () => import('@views/Dashboard.vue'),
          },
          {
            path: 'bi-dashboards',
            name: 'bi-dashboards',
            component: () => import('@views/BiDashboards.vue'),
          },
          {
            path: 'materialOccupancyReport',
            name: 'materialOccupancyReport',
            component: () => import('@views/MaterialOccupancyReport.vue'),
          },
          {
            path: 'counters',
            name: 'counters',
            component: () => import('@views/counters/Counters.vue'),
          },
          {
            path: 'counters/:counterId',
            name: 'countersAnchor',
            component: () => import('@views/counters/Counters.vue'),
          },

          {
            path: 'tag-in-tag-out',
            name: 'tag-in-tag-out',
            beforeEnter: nonWorkerProfileGuard,
            component: () => import('@views/TagInTagOut.vue'),
          },
        ],
      },
      {
        path: '/alerts',
        beforeEnter: authenticationGuard,
        component: () => import('@components/DefaultContainer.vue'),
        children: [
          {
            path: 'list',
            name: 'alerts_list',
            beforeEnter: authenticationGuard,
            component: () => import('@views/Alerts.vue'),
          },
          {
            path: 'configuration',
            name: 'alerts_configuration',
            beforeEnter: authenticationGuard,
            component: () => import('@views/AlertConfigs.vue'),
          },
        ],
      },
      {
        path: '/administration',
        beforeEnter: authenticationGuard,
        component: () => import('@components/DefaultContainer.vue'),
        children: [
          {
            path: 'resources',
            name: 'resources',
            beforeEnter: adminGuard,
            component: () => import('@views/AdministrationResources.vue'),
          },
          {
            path: 'tags/:manufacturerId?',
            name: 'tags',
            beforeEnter: adminGuard,
            component: () => import('@views/AdministrationTags.vue'),
          },
          {
            path: 'resources/create',
            name: 'resources_select_family',
            beforeEnter: adminGuard,
            component: () => import('@views/AdministrationFamilySelect.vue'),
          },
          {
            path: 'resources/create/asset/:family/:subFamily',
            name: 'resources_form',
            beforeEnter: adminGuard,
            component: () => import('@views/AdministrationCreateAsset.vue'),
          },
          {
            path: 'reports',
            name: 'reports',
            beforeEnter: adminGuard,
            component: () =>
              import('@views/administration/reports/ReportsList.vue'),
          },
          {
            path: 'reports/create',
            name: 'reportsFamilySelect',
            beforeEnter: adminGuard,
            component: () =>
              import('@views/administration/reports/ReportsFamilySelect.vue'),
          },
          {
            path: 'reports/create/:family/:subFamily',
            name: 'reports_form',
            beforeEnter: adminGuard,
            component: () =>
              import('@views/administration/reports/ReportsCreate.vue'),
          },
          {
            path: 'labels',
            name: 'labels',
            beforeEnter: adminGuard,
            component: () => import('@views/administration/Labels.vue'),
          },
          {
            path: 'sites',
            name: 'sites',
            beforeEnter: adminGuard,
            component: () => import('@views/administration/Sites.vue'),
          },
          {
            path: 'areas',
            name: 'areas',
            beforeEnter: adminGuard,
            component: () => import('@views/administration/Areas.vue'),
          },
          {
            path: 'counters',
            name: 'counters-admin-view',
            beforeEnter: adminGuard,
            component: () => import('@views/counters/CountersAdminView.vue'),
          },
          {
            path: 'counters/create',
            name: 'counter-admin-create',
            beforeEnter: adminGuard,
            component: () => import('@views/counters/CreateCounterView.vue'),
          },
          {
            path: 'counters/edit/:counterId',
            name: 'counter-admin-edit',
            beforeEnter: adminGuard,
            component: () => import('@views/counters/CreateCounterView.vue'),
          },
        ],
      },
      {
        path: '/:clientId/qr-code/:qrCodeId',
        beforeEnter: authenticationGuard,
        name: 'redirect-qr',
        component: () => import('@components/QrCodeRedirect.vue'),
      },
      {
        path: '/debug-ui',
        beforeEnter: authenticationGuard,
        component: () => import('@components/ui/DebugUi'),
      },
      {
        path: '/experimental-features',
        beforeEnter: authenticationGuard,
        component: () => import('@components/DefaultContainer.vue'),
        children: [
          {
            path: '',
            name: 'experimental-features',
            component: () => import('@common/views/FeatureFlagsView.vue'),
          },
        ],
      },
      {
        path: '*',
        name: 'notFound',
        component: PageNotFound,
      },
    ],
  })

  router.beforeEach(async (_to, _from, next) => {
    /**
     * This route guard ensures that the app is connected to Kuzzle before
     * passing to the next route.
     */
    const offlinePageDelay = 1500
    return new Promise(async (resolve) => {
      setTimeout(() => {
        if (!store.state.app.online) {
          store.commit('app/SET_WAITING_FOR_CONNECTION')
        }
      }, offlinePageDelay)
      const onceConnected = () => {
        store.commit('app/SET_ONLINE')

        setTimeout(() => {
          store.commit('app/UNSET_WAITING_FOR_CONNECTION')
        }, offlinePageDelay)

        kuzzle.removeListener('connected', onceConnected)
        kuzzle.removeListener('reconnected', onceConnected)

        next()
        return resolve()
      }
      if (!store.state.app.online) {
        kuzzle.addListener('connected', onceConnected)
        kuzzle.addListener('reconnected', onceConnected)
      } else {
        onceConnected()
      }
    })
  })

  return router
}
